import type { FieldFields, FieldInstanceFields, PropertyDataType } from "@regrello/graphql-api";
import { isDevServer } from "../../../../../utils/environmentUtils";
import { getErrorMessageWithPayload } from "../../../../../utils/getErrorMessageWithPayload";
import type { ProspectiveSpectrumFieldVersion } from "../types/ProspectiveSpectrumFieldVersion";
import type {
  SpectrumFieldPluginDecorator,
  SpectrumFieldPluginDecorators,
} from "../types/SpectrumFieldPluginDecorator";

export const spectrumFieldPlugins: SpectrumFieldPluginDecorators = [];

export const SpectrumFieldPluginRegistrar = {
  getAllPlugins(): SpectrumFieldPluginDecorators {
    return spectrumFieldPlugins;
  },

  /**
   * Returns the one spectrum-field plugin that is compatible with the provided field.
   *
   * @throws if no compatible spectrum-field plugin is found, or if multiple are found
   * @deprecated since Oct 9, 2025. Instead, please use `getPluginForSpectrumField` to ensure we're
   * using Field Versions when available. All loaded field instances now include an accompanying
   * Field Version that you can use with this lookup function.
   */
  getPluginForField(field: FieldFields): SpectrumFieldPluginDecorator<unknown> {
    const matches: SpectrumFieldPluginDecorators = [];

    for (const spectrumFieldPlugin of spectrumFieldPlugins) {
      if (spectrumFieldPlugin.canProcessField(field)) {
        matches.push(spectrumFieldPlugin);
      }
    }

    if (matches.length === 0) {
      throw new Error(
        getErrorMessageWithPayload(
          "Could not find a spectrum-field plugin that was able to process the current field. " +
            "This is a developer error. " +
            "Please review the `canProcessField` logic in all spectrum-field plugins.",
          { field },
        ),
      );
    }

    if (matches.length > 1) {
      const uniqueUris = new Set(matches.map(({ uri }) => uri));
      if (uniqueUris.size === 1 && isDevServer()) {
        // In Dev mode, the same plugin may be registered multiple times due to Fast Refresh.
        // Just return the freshest one without throwing an error.
        return matches[matches.length - 1];
      }

      throw new Error(
        getErrorMessageWithPayload(
          "Multiple spectrum-field plugins claimed to be able to process the current field. " +
            "Only one spectrum-field plugin should be able to process any given field. " +
            "This is a developer error. " +
            "Please review the `canProcessField` logic in the spectrum-field plugins mentioned here.",
          {
            field,
            ostensiblyCompatibleSpectrumFieldPluginUris: matches.map(({ uri }) => uri),
          },
        ),
      );
    }

    return matches[0];
  },

  /**
   * Returns the one spectrum-field plugin that is compatible with the provided field instance.
   *
   * @throws if no compatible spectrum-field plugin is found, or if multiple are found
   * @deprecated since Oct 9, 2025. Instead, please use `getPluginForSpectrumField` to ensure we're
   * using Field Versions when available. All loaded field instances now include an accompanying
   * Field Version that you can use with this lookup function.
   */
  getPluginForFieldInstance(fieldInstance: FieldInstanceFields): SpectrumFieldPluginDecorator<unknown> {
    const matches: SpectrumFieldPluginDecorators = [];

    for (const spectrumFieldPlugin of spectrumFieldPlugins) {
      if (spectrumFieldPlugin.canProcessFieldInstance(fieldInstance)) {
        matches.push(spectrumFieldPlugin);
      }
    }

    if (matches.length === 0) {
      throw new Error(
        getErrorMessageWithPayload(
          "Could not find a spectrum-field plugin that was able to process the current field instance. " +
            "This is a developer error. " +
            "Please review the `canProcessFieldInstance` logic in all spectrum-field plugins.",
          { fieldInstance },
        ),
      );
    }

    if (matches.length > 1) {
      const uniqueUris = new Set(matches.map(({ uri }) => uri));
      if (uniqueUris.size === 1 && isDevServer()) {
        // In Dev mode, the same plugin may be registered multiple times due to Fast Refresh.
        // Just return the freshest one without throwing an error.
        return matches[matches.length - 1];
      }

      throw new Error(
        getErrorMessageWithPayload(
          "Multiple spectrum-field plugins claimed to be able to process the current field instance. " +
            "Only one spectrum-field plugin should be able to process any given field instance. " +
            "This is a developer error. " +
            "Please review the `canProcessFieldInstance` logic in the spectrum-field plugins mentioned here.",
          {
            fieldInstance,
            ostensiblyCompatibleSpectrumFieldPluginUris: matches.map(({ uri }) => uri),
          },
        ),
      );
    }

    return matches[0];
  },

  /**
   * Returns the one spectrum-field plugin that is compatible with the provided field instance.
   *
   * @throws if no compatible spectrum-field plugin is found, or if multiple are found
   */
  getPluginForSpectrumField(spectrumField: ProspectiveSpectrumFieldVersion): SpectrumFieldPluginDecorator<unknown> {
    const matches: SpectrumFieldPluginDecorators = [];

    for (const spectrumFieldPlugin of spectrumFieldPlugins) {
      if (spectrumFieldPlugin.canProcessSpectrumField(spectrumField)) {
        matches.push(spectrumFieldPlugin);
      }
    }

    if (matches.length === 0) {
      throw new Error(
        "Could not find a spectrum-field plugin that was able to process the current field. " +
          "This is a developer error. " +
          "Please review the `canProcessSpectrumField` logic in all spectrum-field plugins." +
          JSON.stringify(spectrumField, undefined, 2),
      );
    }

    if (matches.length > 1) {
      const uniqueUris = new Set(matches.map(({ uri }) => uri));
      if (uniqueUris.size === 1 && isDevServer()) {
        // In Dev mode, the same plugin may be registered multiple times due to Fast Refresh.
        // Just return the freshest one without throwing an error.
        return matches[matches.length - 1];
      }

      throw new Error(
        getErrorMessageWithPayload(
          "Multiple spectrum-field plugins claimed to be able to process the current field. " +
            "Only one spectrum-field plugin should be able to process any given field. " +
            "This is a developer error. " +
            "Please review the `canProcessSpectrumField` logic in the spectrum-field plugins mentioned here.",
          {
            spectrumField,
            ostensiblyCompatibleSpectrumFieldPluginUris: matches.map(({ uri }) => uri),
          },
        ),
      );
    }

    return matches[0];
  },

  /**
   * Returns an array of spectrum-field plugins that are compatible with the provided
   * `PropertyDataType`. This is useful for determining which select field options (or form fields
   * in general) should be shown to users without having a `FieldInstanceFields` handy for providing
   * to the `getPluginForFieldInstance` function.
   *
   * It may also return an empty array.
   */
  getPluginsForPropertyDataType(propertyDataType: PropertyDataType): SpectrumFieldPluginDecorators {
    const matches: SpectrumFieldPluginDecorators = [];

    for (const spectrumFieldPlugin of spectrumFieldPlugins) {
      if (spectrumFieldPlugin.canProcessPropertyDataType(propertyDataType)) {
        matches.push(spectrumFieldPlugin);
      }
    }

    return matches;
  },

  visitAllPlugins(visitor: (plugin: SpectrumFieldPluginDecorator<unknown>) => void) {
    for (const plugin of spectrumFieldPlugins) {
      visitor(plugin);
    }
  },
};
